import React, { useEffect, useState, useRef } from 'react';
import Cropper from 'react-easy-crop';
import Slider from '@mui/material/Slider';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Grid, Paper, Chip, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropIcon from '@mui/icons-material/Crop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BeatLoader from 'react-spinners/BeatLoader';
import theme from '../../theme/theme';
import { uploadSceneImages, getSceneImagesByProjectIdAndTopicId, deleteSceneFile } from '../../apis/apiList/projectApi';
import { useMessage } from '../../context/MessageContext';

const SceneImageUploadModal = ({ open, onClose, projectId, topicDetail, userDetails, onSave }) => {
    console.log('scene image upload modal opened with topicDetail', topicDetail);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState(null);
  const pendingFileRef = useRef(null);
  const inputRef = useRef(null);
  const { showMessage } = useMessage();

  useEffect(() => {
    async function fetchExisting() {
      if (!open || !projectId || !topicDetail) return;
      setLoading(true);
      try {
        const ids = { projectId, topicId: topicDetail.topicId || topicDetail._id };
        const resp = await getSceneImagesByProjectIdAndTopicId(ids, {
          headers: { 'x-access-token': userDetails?.jwtToken }
        });
        const files = resp?.sceneFiles || [];
        setExistingFiles(files);
      } catch (err) {
        console.error('fetch existing scene images', err);
        showMessage('Failed to load scene images', 'error');
      } finally {
        setLoading(false);
      }
    }
    fetchExisting();
  }, [open, projectId, topicDetail]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = files.filter(f => /\.(jpe?g|png|gif|webp|bmp|tiff)$/i.test(f.name));
    const existingCount = existingFiles.flatMap(s => s.files || []).length;
    const remaining = Math.max(0, 1 - existingCount - selectedFiles.length);
    if (existingCount >= 1) {
      showMessage('You already have an image for this scene. Remove it to add another.', 'error');
      return;
    }
    if (allowed.length === 0) return;

    // Only take the first allowed file
    const file = allowed[0];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        const naturalW = img.naturalWidth;
        const naturalH = img.naturalHeight;
        const reqW = 1320;
        const reqH = 1085;
        const minRatio = 0.7;

        console.log('loaded image dimensions', naturalW, naturalH);
        // If either dimension is below 70% -> block
        if (naturalW < reqW * minRatio || naturalH < reqH * minRatio) {
          showMessage('Image resolution is too low. Please upload an image at least 1320 × 1085.', 'error');
          if (inputRef.current) inputRef.current.value = '';
          return;
        }

        // If either dimension is less than required -> warn but allow
        if (naturalW < reqW || naturalH < reqH) {
          showMessage('This image is smaller than the recommended size (1320 × 1085). The final video quality may be reduced.', 'warning');
        }

        // Passed minimum validation — allow opening crop UI
        pendingFileRef.current = file;
        setImageSrc(dataUrl);
        setCropOpen(true);
      };
      img.onerror = () => {
        showMessage('Failed to read selected image', 'error');
        if (inputRef.current) inputRef.current.value = '';
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dtFiles = Array.from(e.dataTransfer.files || []);
    const fakeEvent = { target: { files: dtFiles } };
    handleFileSelect(fakeEvent);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUpload = async () => {
    const existingCount = existingFiles.flatMap(s => s.files || []).length;
    if (existingCount >= 3) {
      showMessage('You already have 3 images for this scene. Remove one to add more.', 'error');
      return;
    }
    if (selectedFiles.length === 0) {
      showMessage('Please choose at least one image to upload', 'error');
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      selectedFiles.forEach((f) => form.append('sceneImages', f));
      form.append('topicId', topicDetail.topicId || topicDetail._id);
      const resp = await uploadSceneImages(projectId, form, {
        headers: { 'x-access-token': userDetails?.jwtToken, 'Content-Type': 'multipart/form-data' }
      });
      showMessage(resp?.message || 'Uploaded', 'success');
      setSelectedFiles([]);
      if (onSave) onSave(resp);
      onClose();
    } catch (err) {
      console.error('upload scene images', err);
      showMessage(err?.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExistingFile = async (sceneId, file) => {
    if (!sceneId || !file) return;
    try {
      setLoading(true);
      await deleteSceneFile(sceneId, file.fileId, { headers: { 'x-access-token': userDetails?.jwtToken } });
      // update local state by refetching
      const ids = { projectId, topicId: topicDetail.topicId || topicDetail._id };
      const resp = await getSceneImagesByProjectIdAndTopicId(ids, { headers: { 'x-access-token': userDetails?.jwtToken } });
      setExistingFiles(resp?.sceneFiles || []);
      showMessage('Image removed', 'success');
    } catch (err) {
      console.error('delete scene file', err);
      showMessage('Failed to delete image', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSelected = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onCropComplete = (croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
    // generate a small preview showing how the final cropped image will look
    if (imageSrc && croppedAreaPx) {
      generateCroppedPreview(imageSrc, croppedAreaPx).then((url) => setCroppedPreviewUrl(url)).catch(() => setCroppedPreviewUrl(null));
    }
  };

  const getCroppedImg = async (imageSrcParam, pixelCrop, fileName) => {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = imageSrcParam;
    });

    const targetWidth = 1320;
    const targetHeight = 1085;

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    // draw the cropped area onto canvas resized to target dims
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      targetWidth,
      targetHeight
    );

    return await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const newFile = new File([blob], fileName, { type: blob.type });
        resolve(newFile);
      }, 'image/jpeg', 0.92);
    });
  };

  const generateCroppedPreview = async (imageSrcParam, pixelCrop, previewWidth = 300) => {
    if (!pixelCrop) return null;
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = imageSrcParam;
    });

    const reqW = 1320;
    const reqH = 1085;
    const previewHeight = Math.round(previewWidth * (reqH / reqW));

    const canvas = document.createElement('canvas');
    canvas.width = previewWidth;
    canvas.height = previewHeight;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const handleConfirmCrop = async () => {
    try {
      setLoading(true);
      const fileName = pendingFileRef.current ? pendingFileRef.current.name : `cropped_${Date.now()}.jpg`;
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, fileName);
      setSelectedFiles(prev => [...prev, croppedFile]);
      // cleanup
      pendingFileRef.current = null;
      setImageSrc(null);
      setCropOpen(false);
      setCroppedPreviewUrl(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      console.error('crop error', err);
      showMessage('Failed to crop image', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCrop = () => {
    pendingFileRef.current = null;
    setImageSrc(null);
    setCropOpen(false);
    if (inputRef.current) inputRef.current.value = '';
    setCroppedPreviewUrl(null);
  };

  const overrideStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { width: '90%', maxHeight: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Upload Scene Images for {topicDetail?.topicTitle || topicDetail?.title}
        <IconButton onClick={onClose} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '50%', width: 36, height: 36 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'auto', maxHeight: '70vh' }}>
        {loading ? (
          <Box sx={overrideStyle}><BeatLoader color={theme.palette.primary.main} loading={loading} size={20} /></Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left: existing images */}
            <Paper sx={{ flex: 1, p: 2, minHeight: 240 }} elevation={1} onDrop={handleDrop} onDragOver={handleDragOver}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Scene Images ({existingFiles.flatMap(s=>s.files||[]).length}/1)</Typography>
              <Grid container spacing={1}>
                {existingFiles.flatMap(s => (s.files || []).map(f => ({...f, sceneId: s._id}))).map((file, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box sx={{ position: 'relative' }}>
                      <img src={file.fileUrl} alt={file.fileOriginalName} style={{ width: '100%', height: 250, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} onClick={() => { setPreviewSrc(file.fileUrl); setPreviewOpen(true); }} />
                      <IconButton size="small" onClick={() => handleDeleteExistingFile(file.sceneId, file)} sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', width: 28, height: 28, p: '4px' }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Drag & drop an image here to add (or use Choose Image). Max 1 total.</Typography>
            </Paper>

            {/* Right: uploader */}
            <Paper sx={{ width: 320, p: 2 }} elevation={1}>
              <Typography variant="subtitle1">Add Images</Typography>
              <Box sx={{ my: 1 }}>
                <Button variant="outlined" fullWidth onClick={() => inputRef.current && inputRef.current.click()} disabled={existingFiles.flatMap(s => s.files || []).length >= 1}>Choose Image</Button>
                <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
              </Box>

              {/* Crop dialog */}
              <Dialog 
                open={cropOpen} 
                onClose={handleCancelCrop} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{ 
                  sx: { 
                    borderRadius: 2,
                    maxHeight: '90vh'
                  } 
                }}
              >
                <DialogTitle sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  pb: 2.5,
                  pt: 2.5,
                  borderBottom: '2px solid',
                  borderColor: '#e5e7eb'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      bgcolor: '#f3f4f6', 
                      borderRadius: '50%', 
                      p: 1, 
                      display: 'flex'
                    }}>
                      <CropIcon sx={{ color: '#000' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#000' }}>Crop & Resize Image</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>Adjust to 1320 × 1085 pixels</Typography>
                    </Box>
                  </Box>
                  <IconButton 
                    onClick={handleCancelCrop} 
                    size="small"
                    sx={{ 
                      bgcolor: '#f3f4f6',
                      color: '#000',
                      '&:hover': { bgcolor: '#e5e7eb' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 3 }}>
                  <Box sx={{ pt: 3, display: 'flex', gap: 3, height: 550, flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Left: Cropper */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper 
                        elevation={3}
                        sx={{ 
                          position: 'relative', 
                          bgcolor: '#000', 
                          borderRadius: 2,
                          overflow: 'hidden',
                          height: 450,
                          border: '3px solid',
                          borderColor: '#D10000',
                          boxShadow: '0 8px 24px rgba(209, 0, 0, 0.2)'
                        }}
                      >
                        {imageSrc && (
                          <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1320 / 1085}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                          />
                        )}
                      </Paper>
                      
                      {/* Zoom Controls */}
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 2.5, 
                          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: '#e0e7ff'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            bgcolor: '#D10000', 
                            borderRadius: '50%', 
                            p: 0.8, 
                            display: 'flex',
                            boxShadow: '0 2px 8px rgba(209, 0, 0, 0.3)'
                          }}>
                            <ZoomOutIcon fontSize="small" sx={{ color: '#fff' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: '#374151' }}>Zoom Level: {Math.round(zoom * 100)}%</Typography>
                            <Slider 
                              value={zoom} 
                              min={1} 
                              max={3} 
                              step={0.01} 
                              onChange={(e, v) => setZoom(v)}
                              sx={{
                                color: '#D10000',
                                '& .MuiSlider-thumb': {
                                  width: 20,
                                  height: 20,
                                  boxShadow: '0 2px 8px rgba(209, 0, 0, 0.4)',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(209, 0, 0, 0.6)',
                                  }
                                },
                                '& .MuiSlider-track': {
                                  height: 6,
                                },
                                '& .MuiSlider-rail': {
                                  height: 6,
                                  opacity: 0.3,
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ 
                            bgcolor: '#D10000', 
                            borderRadius: '50%', 
                            p: 0.8, 
                            display: 'flex',
                            boxShadow: '0 2px 8px rgba(209, 0, 0, 0.3)'
                          }}>
                            <ZoomInIcon fontSize="small" sx={{ color: '#fff' }} />
                          </Box>
                        </Box>
                        {/* <Typography variant="caption" sx={{ display: 'block', mt: 1.5, textAlign: 'center', color: '#6b7280', fontWeight: 500 }}>
                          💡 Drag image to reposition • Scroll or use slider to zoom
                        </Typography> */}
                      </Paper>
                    </Box>

                    {/* Right: Preview Panel */}
                    <Paper 
                      elevation={3}
                      sx={{ 
                        width: { xs: '100%', md: 360 }, 
                        p: 0,
                        background: 'linear-gradient(135deg, #D10000 0%, #8B0000 100%)',
                        borderRadius: 3,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 10px 40px rgba(209, 0, 0, 0.3)'
                      }}
                    >
                      {/* Preview Header */}
                      <Box sx={{ p: 2.5, pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>Live Preview</Typography>
                          <Chip 
                            label="1320 × 1085" 
                            size="small" 
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(255,255,255,0.25)',
                              color: '#fff',
                              border: '1px solid rgba(255,255,255,0.3)',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block' }}>
                          See your final cropped result
                        </Typography>
                      </Box>

                      {/* Preview Image Area */}
                      <Box sx={{ px: 2.5, pb: 2.5 }}>
                        <Paper
                          elevation={4}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            bgcolor: '#ffffff',
                            backgroundImage: croppedPreviewUrl ? 'none' : 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                            backgroundSize: '16px 16px',
                            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                            borderRadius: 2,
                            border: croppedPreviewUrl ? '3px solid' : '3px dashed',
                            borderColor: croppedPreviewUrl ? '#10b981' : 'rgba(255,255,255,0.4)',
                            // p: 2.5,
                            minHeight: 300,
                            transition: 'all 0.4s ease',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {croppedPreviewUrl ? (
                            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img 
                                src={croppedPreviewUrl} 
                                alt="preview" 
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '280px', 
                                  objectFit: 'contain',
                                  borderRadius: 8,
                                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                  animation: 'fadeIn 0.3s ease-in'
                                }} 
                              />
                              <Box sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8,
                                bgcolor: '#10b981',
                                color: '#fff',
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)'
                              }}>
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>Ready</Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Box sx={{ 
                                bgcolor: '#f3f4f6', 
                                borderRadius: '50%', 
                                width: 80, 
                                height: 80, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                border: '3px dashed #d1d5db'
                              }}>
                                <CropIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                              </Box>
                              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Adjust crop area</Typography>
                              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>Preview will appear here</Typography>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                      
                      {/* Features List */}
                      {/* <Box sx={{ 
                        bgcolor: 'rgba(255,255,255,0.15)', 
                        backdropFilter: 'blur(10px)',
                        p: 2.5,
                        borderTop: '1px solid rgba(255,255,255,0.2)'
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ 
                              bgcolor: 'rgba(16, 185, 129, 0.9)', 
                              borderRadius: '50%', 
                              width: 24, 
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)'
                            }}>
                              <CheckCircleIcon sx={{ fontSize: 14, color: '#fff' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>Final resolution: 1320×1085px</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ 
                              bgcolor: 'rgba(16, 185, 129, 0.9)', 
                              borderRadius: '50%', 
                              width: 24, 
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)'
                            }}>
                              <CheckCircleIcon sx={{ fontSize: 14, color: '#fff' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>Optimized for video quality</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ 
                              bgcolor: 'rgba(16, 185, 129, 0.9)', 
                              borderRadius: '50%', 
                              width: 24, 
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)'
                            }}>
                              <CheckCircleIcon sx={{ fontSize: 14, color: '#fff' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>Maintains aspect ratio</Typography>
                          </Box>
                        </Box>
                      </Box> */}
                    </Paper>
                  </Box>
                </DialogContent>
                
                <DialogActions sx={{ 
                  px: 3, 
                  py: 2.5, 
                  background: 'linear-gradient(to bottom, transparent, rgba(209, 0, 0, 0.05))',
                  borderTop: '2px solid',
                  borderColor: '#fee2e2',
                  gap: 1.5
                }}>
                  <Button 
                    onClick={handleCancelCrop}
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#cbd5e1',
                      color: '#64748b',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': {
                        borderColor: '#94a3b8',
                        bgcolor: 'rgba(100, 116, 139, 0.05)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleConfirmCrop} 
                    disabled={loading || !croppedPreviewUrl}
                    size="large"
                    startIcon={loading ? <BeatLoader color="#fff" size={8} /> : <CheckCircleIcon />}
                    sx={{ 
                      minWidth: 180,
                      background: '#D10000',
                      fontWeight: 700,
                      px: 4,
                      boxShadow: '0 4px 16px rgba(209, 0, 0, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B80000 0%, #7A0000 100%)',
                        boxShadow: '0 6px 20px rgba(209, 0, 0, 0.5)'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    {loading ? 'Processing...' : 'Apply Crop'}
                  </Button>
                </DialogActions>
              </Dialog>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedFiles.map((f, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img src={URL.createObjectURL(f)} alt={f.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                    <IconButton size="small" onClick={() => handleRemoveSelected(i)} sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', width: 24, height: 24, p: '3px' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">Remaining slots: {Math.max(0, 1 - existingFiles.flatMap(s => s.files || []).length - selectedFiles.length)}</Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={loading || existingFiles.flatMap(s => s.files || []).length >= 1 || selectedFiles.length === 0}
        >
          Upload
        </Button>
      </DialogActions>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" PaperProps={{ sx: { width: '80%', maxHeight: '90vh' } }}>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          {previewSrc && (
            <img src={previewSrc} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh', width: 'auto', height: 'auto', objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  )
};

export default SceneImageUploadModal;
